import { zodResolver } from '@hookform/resolvers/zod'
import { HandArrowDownIcon, PencilIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useCheckoutPackage } from '@/api/packages/checkout-package'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import { PackageModel } from '@/models'
import { makeCheckoutPackageSchema } from '@/pages/packages/checkout-package-modal/checkout-package-modal.constants'
import { CheckoutPackageFormData } from '@/pages/packages/checkout-package-modal/checkout-package-modal.constants.types'
import { getApiErrorMessage } from '@/utils'

import * as S from './checkout-package-modal.styled'

export interface CheckoutPackageModalProps {
  onClose: () => void
  selectedPackageData: PackageModel
}

export const CheckoutPackageModal = ({
  onClose,
  selectedPackageData,
}: CheckoutPackageModalProps) => {
  const { t } = useTranslation(['packages'])
  const [isEdit, setIsEdit] = useState(false)
  const isInitialComment = !!selectedPackageData.comment

  const { mutateAsync: checkoutPackage } = useCheckoutPackage()

  const form = useForm<CheckoutPackageFormData>({
    defaultValues: {
      comment: selectedPackageData.comment || '',
    },
    mode: 'onTouched',
    resolver: zodResolver(makeCheckoutPackageSchema(t)),
  })

  const comment = useWatch({
    control: form.control,
    name: 'comment',
  })

  const handleConfirm = async () => {
    try {
      const data = form.getValues()
      await checkoutPackage({
        comment: data.comment || '',
        id: selectedPackageData.id,
        signature: 'packageSignedWeb',
      })
      toastFactory.success(t('packages:checkoutModal.toastSuccess'))
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  return (
    <ConfirmationPopup
      autoWidth
      title={t('packages:checkoutModal.title')}
      variant="info"
      confirmText={t('packages:checkoutModal.confirm')}
      cancelText={t('packages:checkoutModal.cancel')}
      confirmIcon={HandArrowDownIcon}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <FormProvider {...form}>
        <S.ModalContent>
          <Typography size="text-huge" color="black700">
            {t('packages:checkoutModal.body')}
          </Typography>

          {isInitialComment ? (
            <S.CommentSection>
              {isEdit ? (
                <TextAreaFormField
                  name="comment"
                  label={t('packages:checkoutModal.comment.editLabel')}
                  size="small"
                  rows={3}
                />
              ) : (
                <S.ExistingComment>
                  <Typography
                    as="p"
                    color="black800"
                    size="text-small"
                    weight="regular"
                    whiteSpace="pre-wrap"
                  >
                    {comment}
                  </Typography>
                  <S.EditButtonWrapper>
                    <Button use="secondary" size="small" onClick={() => setIsEdit(true)}>
                      <Icon icon={PencilIcon} />
                      {t('packages:checkoutModal.comment.edit')}
                    </Button>
                  </S.EditButtonWrapper>
                </S.ExistingComment>
              )}
              {!isEdit && (
                <Typography color="black600" size="text-small">
                  {t('packages:checkoutModal.comment.existingNote')}
                </Typography>
              )}
            </S.CommentSection>
          ) : (
            <S.CommentSection>
              <TextAreaFormField
                name="comment"
                label={t('packages:checkoutModal.comment.label')}
                size="small"
                rows={3}
              />
            </S.CommentSection>
          )}
        </S.ModalContent>
      </FormProvider>
    </ConfirmationPopup>
  )
}
